import React, { useMemo, useCallback } from 'react';
import { BookOpen, AlertCircle } from 'lucide-react';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useInfiniteGuides, GuidesFilters } from '@/hooks/useGuides';
import { useTranslation } from '@/hooks/useTranslation';
import GuideCard from './GuideCard';
import GuideCardSkeleton from './GuideCardSkeleton';

interface GuidesContentProps {
  filters: GuidesFilters;
  selectedLanguage: string;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

const GuidesContent: React.FC<GuidesContentProps> = ({
  filters,
  selectedLanguage,
  hasActiveFilters,
  onClearFilters,
}) => {
  const { t } = useTranslation('guides');

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } = useInfiniteGuides(filters);

  const guides = useMemo(() => {
    return (
      data?.pages.flatMap((page) => {
        return page.data || [];
      }) || []
    );
  }, [data]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loaderRef = useInfiniteScroll({
    hasMore: !!hasNextPage && !isFetchingNextPage,
    onLoadMore: handleLoadMore,
  });

  return (
    <div className="flex-1">
      {/* Error State */}
      {error && (
        <div className="mb-8 bg-red-50 dark:bg-red-950/30 border-l-4 border-red-500 rounded-xl p-6 flex items-start gap-4 shadow-lg backdrop-blur-sm">
          <div className="flex-shrink-0 p-2 rounded-lg bg-red-100 dark:bg-red-900/50">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-red-900 dark:text-red-200 mb-1">{t('states.error.title')}</h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error.message || t('states.error.message')}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && guides.length === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <GuideCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && guides.length === 0 && !error && (
        <div className="relative overflow-hidden bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-800/50 p-16 text-center shadow-xl">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5" />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-950 dark:to-purple-950 mb-6">
              <BookOpen className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{t('states.empty.title')}</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              {hasActiveFilters ? t('states.empty.withFilters') : t('states.empty.withoutFilters')}
            </p>
            {hasActiveFilters && (
              <button
                onClick={onClearFilters}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('states.empty.clearFilters')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Guides Grid */}
      {guides.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
            {guides.map((guide) => (
              <GuideCard key={guide.id} guide={guide} language={selectedLanguage === 'all' ? 'en' : selectedLanguage} />
            ))}
          </div>

          {/* Infinite Scroll Loader */}
          <div ref={loaderRef} className={`mt-12 ${hasNextPage ? 'min-h-[100px]' : ''}`}>
            {hasNextPage && isFetchingNextPage && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
                {Array.from({ length: 3 }).map((_, i) => (
                  <GuideCardSkeleton key={`skeleton-${i}`} />
                ))}
              </div>
            )}
          </div>

          {/* End of Results */}
          {!hasNextPage && (
            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-full border border-gray-200/50 dark:border-gray-800/50 shadow-lg">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('states.endOfResults')}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GuidesContent;
